import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { List, OrderedMap } from 'immutable';
import cx from 'classnames';
import VisibilitySensor from 'react-visibility-sensor';

import ApplePayButton from 'components/common/buttons/apple-pay-button';
import SavedPaymentMethod from 'components/checkout/payment-information/saved-payment-method';

import NewPaymentMethod from 'containers/checkout/payment-information/new-payment-method';

import { Location } from 'models/locations';
import Brand from 'models/brand';
import PaymentMethod from 'models/payment-method';
import User from 'models/user';

import { dataCollectorShape, braintreeClientInstanceShape, paypalInstanceShape, applePayInstanceShape } from 'lib/common/prop-types';
import { PAYMENT_DETAILS_IMPRESSION, NEW_PAYMENT_METHOD_IMPRESSION, NEW_PAYMENT_METHOD_CLICK } from 'lib/analytics/events';
import RequestQueue from 'lib/api/request-queue';

import changePaymentMethod from 'action-creators/payment-methods/change-payment-method';
import getPreview from 'action-creators/checkout/get-preview';
import getPaymentToken from 'action-creators/payment-methods/get-payment-token';
import modifyRequiredFieldListCreator from 'action-creators/payment-methods/modify-required-field-list';
import setError from 'action-creators/checkout/set-error';
import trackEventCreator from 'action-creators/analytics/track-event';

const propTypes = {
  applePayInstance: applePayInstanceShape,
  braintreeClientInstance: braintreeClientInstanceShape,
  braintreeToken: PropTypes.string,
  brand: PropTypes.instanceOf(Brand).isRequired,
  changePaymentMethod: PropTypes.func.isRequired,
  checkoutTotal: PropTypes.number,
  dataCollector: dataCollectorShape,
  display: PropTypes.bool,
  getPaymentToken: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  location: PropTypes.instanceOf(Location).isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  newPayment: PropTypes.bool.isRequired,
  paymentMethods: PropTypes.instanceOf(OrderedMap),
  paypalInstance: paypalInstanceShape,
  requestQueue: PropTypes.instanceOf(RequestQueue).isRequired,
  selectedPaymentMethod: PropTypes.instanceOf(PaymentMethod),
  setError: PropTypes.func.isRequired,
  setUserInfo: PropTypes.func.isRequired,
  submitCheckout: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  user: PropTypes.instanceOf(User).isRequired,
};

const defaultProps = {
  applePayInstance: null,
  braintreeClientInstance: null,
  braintreeToken: null,
  checkoutTotal: 0,
  dataCollector: null,
  display: true,
  paymentMethods: OrderedMap(),
  paypalInstance: null,
  selectedPaymentMethod: null,
  trackEvent: () => {},
};

class PaymentInformation extends Component {
  constructor(props) {
    super(props);

    this.handleSelectPaymentMethod = this.handleSelectPaymentMethod.bind(this);
    this.handleNewPayment = this.handleNewPayment.bind(this);
    this.handleNewPaymentMethodVisibilityChange = this.handleNewPaymentMethodVisibilityChange.bind(this);

    this.state = {
      paymentMethods: new OrderedMap(),
    };
  }

  componentDidMount() {
    this.props.getPaymentToken();
  }

  componentWillReceiveProps(nextProps) {
    // if payment methods are provided by the global state,
    // but there's none selected AND the user hasn't selected a new payment method,
    // just select the first payment method

    if (
      nextProps.paymentMethods &&
      nextProps.paymentMethods.size &&
      !nextProps.selectedPaymentMethod &&
      !nextProps.newPayment
    ) {
      if (nextProps.paymentMethods !== this.state.paymentMethods) {
        this.props.trackEvent({
          ...PAYMENT_DETAILS_IMPRESSION,
          properties: {
            PaymentNames: nextProps.paymentMethods.map(paymentMethod => paymentMethod.type).join(','),
            PaymentIDs: nextProps.paymentMethods.map(paymentMethod => paymentMethod.id).join(','),
          },
        });
        this.setState({ paymentMethods: nextProps.paymentMethods });
        this.handleSelectPaymentMethod(nextProps.paymentMethods.first());
      }
    }
  }

  handleSelectPaymentMethod(paymentMethod) {
    this.props.setError({ error: {} });
    this.props.changePaymentMethod({
      selectedPaymentMethod: paymentMethod,
      isPaypal: false,
    });
  }

  handleNewPayment(e) {
    e.preventDefault();
    this.props.changePaymentMethod({
      newPayment: true,
    });
    this.props.trackEvent(NEW_PAYMENT_METHOD_CLICK);
  }

  handleNewPaymentMethodVisibilityChange(visible) {
    if (visible) {
      this.props.trackEvent(NEW_PAYMENT_METHOD_IMPRESSION);
    }
  }

  renderSavedPaymentMethods() {
    const newPaymentRadioClassNames = cx({
      radio: true,
      selected: this.props.newPayment,
      checkoutitem: true,
      row: true,
    });

    let savedPaymentMethods = List();

    this.props.paymentMethods.forEach((paymentMethod) => {
      savedPaymentMethods = savedPaymentMethods.concat(
        <SavedPaymentMethod
          key={`pm-${paymentMethod.id}`}
          paymentMethod={paymentMethod}
          user={this.props.user}
          selected={paymentMethod === this.props.selectedPaymentMethod}
          onSelect={this.handleSelectPaymentMethod}
          brand={this.props.brand}
        />,
      );
    });

    return (
      <div className="list-unstyled margin-bottom-30" id="reserve-use-stored">
        {savedPaymentMethods}
        <div className={newPaymentRadioClassNames} key="pm-new" onClick={this.props.newPayment ? null : this.handleNewPayment}>
          <VisibilitySensor onChange={this.handleNewPaymentMethodVisibilityChange} partialVisibility>
            <Fragment>
              <div className="col-xs-1">
                <span className="icons">
                  <span className="add-on-radio" />
                </span>
              </div>
              <div className="col-xs-10 padding-0">
                <span className="payment-data">
                  <FormattedMessage
                    id="checkout.different-payment"
                    defaultMessage="Use a different payment method"
                  />
                </span>
              </div>
            </Fragment>
          </VisibilitySensor>
        </div>
      </div>
    );
  }

  renderNewPaymentMethod() {
    return (
      <NewPaymentMethod
        user={this.props.user}
        submitButton={get(this.props, 'submitButton.btnRef', null)}
        braintreeClientInstance={this.props.braintreeClientInstance}
        paypalInstance={this.props.paypalInstance}
        dataCollector={this.props.dataCollector}
        submitCheckout={this.props.submitCheckout}
      />
    );
  }

  render() {
    if (!parseFloat(this.props.checkoutTotal) || !this.props.display) { return null; }
    const {
      paymentMethods,
      braintreeToken,
      newPayment,
      requestQueue,
      user,
      location,
      locale,
    } = this.props;

    const canSubmit = requestQueue.length() === 0;

    const panelClasses = cx({
      'payment-details-panel': true,
      'border-color-light-grey': true,
      'border-side-top': true,
      'border-style-solid': true,
      'padding-top-20': true,
    });

    return (
      <div id="payment-details" className={panelClasses}>
        <h2 className="display-sm-inline-block display-xs-block margin-bottom-sm-15 margin-top-xs-0 margin-top-xs-5 margin-bottom-xs-15 text-color-dark-slate text-size-20 text-weight-bold">Payment Method</h2>
        <div className="row">
          <div className="col-md-6 col-xs-12">
            <ApplePayButton
              submitCheckout={this.props.submitCheckout}
              checkoutTotal={this.props.checkoutTotal}
              applePayInstance={this.props.applePayInstance}
              dataCollector={this.props.dataCollector}
              orLabel="after"
              className="checkout-margin-left-md text-center"
              display={!(paymentMethods.size) && user.isLoggedIn()}
              canSubmit={canSubmit}
              setUserInfo={this.props.setUserInfo}
              modifyRequiredFieldList={this.props.modifyRequiredFieldList}
              changePaymentMethod={this.props.changePaymentMethod}
              trackEvent={this.props.trackEvent}
              locale={locale}
              location={location}
              position="0"
            />
          </div>
        </div>
        <div className="payment">
          <div id="payment-info">
            <div id="billingContainer">
              { (paymentMethods && paymentMethods.size > 0) ? this.renderSavedPaymentMethods() : null }
              { braintreeToken && (paymentMethods.size === 0 || newPayment) ? this.renderNewPaymentMethod() : null }
            </div>
          </div>
        </div>
      </div>
    );
  }
}


PaymentInformation.propTypes = propTypes;
PaymentInformation.defaultProps = defaultProps;
const mapStateToProps = (state) => {
  const {
    checkoutTotal,
  } = state.checkout;

  const {
    newPayment,
    paymentMethods,
    selectedPaymentMethod,
    braintreeToken,
  } = state.paymentMethods;

  const {
    user,
  } = state.account;

  const {
    brand,
  } = state.brand;

  const {
    requestQueue,
  } = state.requests;

  const {
    insights,
  } = state.analytics;

  return {
    checkoutTotal,
    newPayment,
    paymentMethods,
    selectedPaymentMethod,
    braintreeToken,
    user,
    requestQueue,
    brand,
    insights,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    getPaymentToken,
    getPreview,
    changePaymentMethod,
    modifyRequiredFieldList: modifyRequiredFieldListCreator,
    setError,
    trackEvent: trackEventCreator,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(PaymentInformation);
